/**
 * Feb 2026 R293zn · Linked documents column
 *
 * Reusable badge+popover widget showing the full Quote-to-Cash
 * lineage (RFQ · EST · QT · SO · INV · CN) for one doc row.
 *
 * Feb 2026 R293zr · Switched the custom absolute popover to shadcn's
 * Popover (Radix portal) so the dropdown escapes the dense Sales
 * table's `overflow-hidden` cells and never gets clipped by sibling
 * rows or the scrolling container.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ChevronDown, ChevronRight, Link as LinkIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const KIND_META = {
  rfqs:         { label: "RFQ", tone: "bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-900/60 dark:text-slate-200 dark:border-slate-700" },
  estimates:    { label: "EST", tone: "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/50 dark:text-amber-200 dark:border-amber-800" },
  quotations:   { label: "QT",  tone: "bg-violet-100 text-violet-900 border-violet-300 dark:bg-violet-950/50 dark:text-violet-200 dark:border-violet-800" },
  sales_orders: { label: "SO",  tone: "bg-sky-100 text-sky-900 border-sky-300 dark:bg-sky-950/50 dark:text-sky-200 dark:border-sky-800" },
  invoices:     { label: "INV", tone: "bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-950/50 dark:text-emerald-200 dark:border-emerald-800" },
  credit_notes: { label: "CN",  tone: "bg-rose-100 text-rose-900 border-rose-300 dark:bg-rose-950/50 dark:text-rose-200 dark:border-rose-800" },
};
const KIND_ORDER = ["rfqs", "estimates", "quotations", "sales_orders", "invoices", "credit_notes"];

const KIND_TO_DOC_TYPE = {
  quotations: "estimate",
  sales_orders: "sales_order",
  invoices: "invoice",
  credit_notes: "credit_note",
};

function endpointFor(source, ws, id) {
  if (source === "rfq") return `/gst/workspaces/${ws}/rfqs/${id}/chain`;
  if (source === "estimate") return `/gst/workspaces/${ws}/estimates/${id}/chain`;
  return `/gst/workspaces/${ws}/documents/${id}/chain`;
}

export default function LinkedBadges({
  ws, docId, source = "gst", preloaded = null, onOpen, compact = false,
}) {
  const [chain, setChain] = useState(preloaded || null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  // Fetch on first interaction unless preloaded.
  const ensureLoaded = async () => {
    if (chain || loading) return;
    try {
      setLoading(true);
      const r = await api.get(endpointFor(source, ws, docId));
      setChain(r.data);
    } catch (e) {
      setChain({ counts: {}, rfqs: [], estimates: [], quotations: [], sales_orders: [], invoices: [], credit_notes: [] });
    } finally { setLoading(false); }
  };

  useEffect(() => { if (preloaded) setChain(preloaded); }, [preloaded]);

  const counts = chain?.counts || {};
  const totalLinked =
    (counts.rfqs || 0) + (counts.estimates || 0) + (counts.quotations || 0) +
    (counts.sales_orders || 0) + (counts.invoices || 0) + (counts.credit_notes || 0);

  // Shown badges: those with count > 0; if no chain yet, show a stub
  const badges = KIND_ORDER
    .map((k) => ({ k, n: counts[k] || (chain?.[k]?.length || 0), rows: chain?.[k] || [] }))
    .filter((b) => b.n > 0);

  const openHit = (kind, hit) => {
    setOpen(false);
    onOpen?.({ kind, docType: KIND_TO_DOC_TYPE[kind] || kind, ...hit });
  };

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        if (v) ensureLoaded();
        setOpen(v);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          onMouseEnter={ensureLoaded}
          className="inline-flex items-center gap-0.5 group"
          data-testid={`linked-badges-trigger-${docId}`}
        >
          {badges.length === 0 && !loading ? (
            <span className="text-[10px] text-muted-foreground/60 italic">—</span>
          ) : (
            badges.map((b) => {
              const meta = KIND_META[b.k];
              return (
                <span
                  key={b.k}
                  className={`text-[9px] font-bold uppercase tracking-wider border rounded px-1 py-[1px] ${meta.tone} group-hover:ring-1 group-hover:ring-primary/40`}
                  data-testid={`linked-badge-${b.k}-${docId}`}
                  title={`${b.n} ${meta.label}`}
                >
                  {meta.label}
                  {b.n > 1 && <span className="ml-0.5 opacity-80">{b.n}</span>}
                </span>
              );
            })
          )}
          {loading && (
            <span className="text-[10px] text-muted-foreground animate-pulse">…</span>
          )}
          {!compact && totalLinked > 1 && (
            <ChevronDown className="h-3 w-3 ml-0.5 text-muted-foreground" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={4}
        className="w-[320px] max-h-[320px] overflow-auto p-2 text-xs z-[80]"
        data-testid={`linked-popover-${docId}`}
      >
        <div className="flex items-center justify-between mb-1 px-1">
          <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
            <LinkIcon className="h-3 w-3" /> Linked documents
          </div>
        </div>
        {totalLinked === 0 ? (
          <div className="text-center py-3 text-muted-foreground">No linked documents</div>
        ) : (
          KIND_ORDER.map((k) => {
            const rows = chain?.[k] || [];
            if (rows.length === 0) return null;
            const meta = KIND_META[k];
            return (
              <div key={k} className="mb-1.5">
                <div className="flex items-center gap-1 px-1 mb-0.5">
                  <span className={`text-[9px] font-bold uppercase tracking-wider border rounded px-1 py-[1px] ${meta.tone}`}>
                    {meta.label}
                  </span>
                  <span className="text-[10px] text-muted-foreground">{rows.length}</span>
                </div>
                <ul className="space-y-0.5">
                  {rows.map((r) => (
                    <li key={r.id}>
                      <button
                        type="button"
                        onClick={() => openHit(k, r)}
                        className="w-full text-left px-2 py-1 rounded hover:bg-muted/60 flex items-center justify-between gap-2"
                        data-testid={`linked-popover-${k}-${r.id}`}
                      >
                        <span className="font-mono truncate">{r.no || r.title || r.id.slice(0, 8)}</span>
                        <span className="text-[10px] uppercase text-muted-foreground shrink-0">{r.status || ""}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })
        )}
      </PopoverContent>
    </Popover>
  );
}
